import React, { memo, useMemo } from 'react';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import {
  TextField, Chip, InputLabel, Button,
} from '@picsio/ui';
import { OpenIn } from '@picsio/icons';
import DetailsBlock from '../DetailsBlock';
import Placeholder from '../DetailsBlock/Placeholder';
import { formatWithUserZone } from '../../shared/utils';
import {
  getTextFromRichText, UNITS_VOLUME, UNITS_WEIGHT, UNITS_DIMENSION, DEFAULT_META, EXCLUDE_KEYS,
} from './helpers';

import s from './styles.module.scss';

const ProductMeta = ({
  meta = DEFAULT_META,
  detailsPanelVisibility,
  toggleVisibility,
  blockName,
  blockTitle,
}) => {
  const userTz = useSelector((state) => state.user?.settings?.timeZone?.userTimeZone);
  const dataToDisplay = useMemo(() => meta.filter((item) => !EXCLUDE_KEYS.includes(item.key)), [meta]);

  return (
    <DetailsBlock
      dataQa="productMeta"
      detailsPanelVisibility={detailsPanelVisibility}
      toggleVisibility={toggleVisibility}
      blockName={blockName}
      blockTitle={blockTitle}
    >
      <If condition={dataToDisplay.length === 0}>
        <Placeholder>
          No meta fields
        </Placeholder>
      </If>
      {dataToDisplay.map((item) => {
        const { key } = item;
        const label = key.replace(/\b\w/g, (l) => l.toUpperCase()).replaceAll('_', ' ');
        let { value } = item;

        try {
          value = JSON.parse(item.value);
          if (Array.isArray(value)) value = value.join(' • ');
          if (typeof value.value !== 'undefined') {
            value = value.value;
          }
        } catch (e) {
          // do nothing
        }

        /** JSON */
        if (item.type === 'json') {
          value = item.value;
        }

        /** Link */
        if (item.type === 'link') {
          try {
            const parsed = JSON.parse(item.value);
            if (parsed.url) value = parsed;
            return (
              <div className={s.rowChips} key={key}>
                <InputLabel margin>{label}</InputLabel>
                <div className={s.chips}>
                  <Button color="primary" component="a" componentProps={{ href: parsed.url, target: '_blank' }} endIcon={<OpenIn />}>{parsed.text}</Button>
                </div>
              </div>
            );
          } catch (e) {
            // do nothing
          }
        }

        /** Links */
        if (item.type === 'list.link') {
          try {
            const parsed = JSON.parse(item.value);
            if (Array.isArray(parsed)) value = parsed;
            return (
              <div className={s.rowChips} key={key}>
                <InputLabel margin>{label}</InputLabel>
                <div className={s.chips}>
                  {value.map((link, idx) => {
                    if (idx === 0) return <Button key={link.url + link.text} color="primary" component="a" componentProps={{ href: link.url, target: '_blank' }} endIcon={<OpenIn />}>{link.text}</Button>;
                    return (
                      <React.Fragment key={link.url + link.text}>
                        <span> • </span>
                        <Button color="primary" component="a" componentProps={{ href: link.url, target: '_blank' }} endIcon={<OpenIn />}>{link.text}</Button>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            );
          } catch (e) {
            // do nothing
          }
        }

        if (item.type === 'money') {
          try {
            const parsed = JSON.parse(item.value);
            value = `${parsed.amount} ${parsed.currency_code}`;
            return (
              <div className={s.row} key={key}>
                <TextField
                  value={value}
                  label={label}
                  readOnly
                />
              </div>
            );
          } catch (e) {
            // do nothing
          }
        }

        if (item.type === 'date_time') {
          try {
            value = formatWithUserZone(item.value, userTz);
            return (
              <div className={s.row} key={key}>
                <TextField
                  value={value}
                  label={label}
                  readOnly
                />
              </div>
            );
          } catch (e) {
            // do nothing
          }
        }

        if (item.type === 'list.date_time') {
          try {
            const parsed = JSON.parse(item.value);
            value = parsed.map((d) => formatWithUserZone(d, userTz)).join(' • ');
            return (
              <div className={s.row} key={key}>
                <TextField
                  value={value}
                  label={label}
                  readOnly
                />
              </div>
            );
          } catch (e) {
            // do nothing
          }
        }

        if (item.type === 'date') {
          try {
            value = dayjs(item.value).format('ll');
            return (
              <div className={s.row} key={key}>
                <TextField
                  value={value}
                  label={label}
                  readOnly
                />
              </div>
            );
          } catch (e) {
            // do nothing
          }
        }

        if (item.type === 'list.date') {
          try {
            const parsed = JSON.parse(item.value);
            value = parsed.map((d) => dayjs(d).format('ll')).join(' • ');
            return (
              <div className={s.row} key={key}>
                <TextField
                  value={value}
                  label={label}
                  readOnly
                />
              </div>
            );
          } catch (e) {
            // do nothing
          }
        }

        if (item.type === 'rich_text_field') {
          try {
            const parsed = JSON.parse(item.value);
            value = getTextFromRichText(parsed);
            return (
              <div className={s.row} key={key}>
                <TextField
                  value={value}
                  label={label}
                  readOnly
                />
              </div>
            );
          } catch (e) {
            // do nothing
          }
        }

        if (item.type === 'rating') {
          try {
            const parsed = JSON.parse(item.value);
            value = `${parsed.value} out of ${parsed.scale_max}`;
            return (
              <div className={s.row} key={key}>
                <TextField
                  value={value}
                  label={label}
                  readOnly
                />
              </div>
            );
          } catch (e) {
            // do nothing
          }
        }

        if (item.type === 'list.rating') {
          try {
            const parsed = JSON.parse(item.value);
            value = parsed.map((r) => `${r.value} out of ${r.scale_max}`).join(' • ');
            return (
              <div className={s.row} key={key}>
                <TextField
                  value={value}
                  label={label}
                  readOnly
                />
              </div>
            );
          } catch (e) {
            // do nothing
          }
        }

        if (item.type === 'dimension') {
          try {
            const parsed = JSON.parse(item.value);
            value = `${parsed.value} ${UNITS_DIMENSION[parsed.unit] || parsed.unit}`;
            return (
              <div className={s.row} key={key}>
                <TextField
                  value={value}
                  label={label}
                  readOnly
                />
              </div>
            );
          } catch (e) {
            // do nothing
          }
        }

        if (item.type === 'list.dimension') {
          try {
            const parsed = JSON.parse(item.value);
            value = parsed.map((d) => `${d.value} ${UNITS_DIMENSION[d.unit] || d.unit}`).join(' • ');
            return (
              <div className={s.row} key={key}>
                <TextField
                  value={value}
                  label={label}
                  readOnly
                />
              </div>
            );
          } catch (e) {
            // do nothing
          }
        }

        if (item.type === 'weight') {
          try {
            const parsed = JSON.parse(item.value);
            value = `${parsed.value} ${UNITS_WEIGHT[parsed.unit] || parsed.unit}`;
            return (
              <div className={s.row} key={key}>
                <TextField
                  value={value}
                  label={label}
                  readOnly
                />
              </div>
            );
          } catch (e) {
            // do nothing
          }
        }

        if (item.type === 'list.weight') {
          try {
            const parsed = JSON.parse(item.value);
            value = parsed.map((d) => `${d.value} ${UNITS_WEIGHT[d.unit] || d.unit}`).join(' • ');
            return (
              <div className={s.row} key={key}>
                <TextField
                  value={value}
                  label={label}
                  readOnly
                />
              </div>
            );
          } catch (e) {
            // do nothing
          }
        }

        if (item.type === 'volume') {
          try {
            const parsed = JSON.parse(item.value);
            value = `${parsed.value} ${UNITS_VOLUME[parsed.unit] || parsed.unit}`;
            return (
              <div className={s.row} key={key}>
                <TextField
                  value={value}
                  label={label}
                  readOnly
                />
              </div>
            );
          } catch (e) {
            // do nothing
          }
        }

        if (item.type === 'list.volume') {
          try {
            const parsed = JSON.parse(item.value);
            value = parsed.map((d) => `${d.value} ${UNITS_VOLUME[d.unit] || d.unit}`).join(' • ');
            return (
              <div className={s.row} key={key}>
                <TextField
                  value={value}
                  label={label}
                  readOnly
                />
              </div>
            );
          } catch (e) {
            // do nothing
          }
        }

        /** If it's a single reference */
        if (item.reference) {
          value = [];
          if (item.reference.displayName || item.reference.title) value.push(item.reference.displayName || item.reference.title);
          if (!value.length) value = 'Extended access required to display value';
        }

        /** If it's a multi-reference */
        if (item.references && Array.isArray(item.references)) {
          value = item.references.map((r) => r?.displayName || r?.title).filter(Boolean);
          if (value.length === 0) value = 'Extended access required to display value';
        }

        /** If it's a reference but we don't have access to it */
        if (item.type.endsWith('_reference') && !item.reference && !item.references) {
          value = 'Extended access required to display value';
        }

        /** Render chips for references */
        if (Array.isArray(value)) {
          return (
            <div className={s.rowChips} key={key}>
              <InputLabel margin>{label}</InputLabel>
              <div className={s.chips}>
                {value.map((v) => <Chip key={v} text={v} />)}
              </div>
            </div>
          );
        }

        /** Text field for everything else */
        return (
          <div className={s.row} key={key}>
            <TextField
              value={value}
              label={label}
              readOnly
            />
          </div>
        );
      })}
    </DetailsBlock>
  );
};

export default memo(ProductMeta);
